import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useEntityDetails } from '../hooks/useEntityDetails';
import { ToggleGroup, ToggleGroupItem } from '../components/ui/toggle-group';
import { formatUnits } from 'viem';
import { useProduct } from '../contexts/ProductContext';
import { ProductDetails } from '../hooks/useEntityDetails';
import { Bank, Factory, Cube, Question, CaretLeft, CaretRight } from '@phosphor-icons/react';
import { useContractRead } from 'wagmi';
import { CONFIG } from '../config';
import { Abi } from 'viem';
import { useGetProperty, useGetImage } from '../utils/properties';
import { ProductCard } from '../components/ProductCard';
import { EntityCard } from '../components/EntityCard';
import { EntityDetails } from '../hooks/useEntityDetails';

// Map product types to icons
const PRODUCT_TYPE_ICONS: Record<string, typeof Bank> = {
  'TrinityBond': Bank,
  'TrinityFactory': Factory,
  'TrinityProduct': Cube,
  'Unknown': Question,
};

type GroupBy = 'entity' | 'type';

interface ProductWithEntity extends ProductDetails {
  entityName: string;
}

interface PropertyValue {
  key: string;
  valueType: number;
  stringValue: string;
  uintValue: bigint;
  boolValue: boolean;
}

function ProductImage({ productAddress }: { productAddress: string }) {
  const imageData = useGetImage(
    productAddress as `0x${string}`,
    !!productAddress
  );
  const [productType, setProductType] = useState<string>('Unknown')

  // Get product type
  const { data: className } = useContractRead({
    address: productAddress as `0x${string}`,
    abi: CONFIG.TRINITY_PRODUCT_ABI.abi as unknown as Abi,
    functionName: 'className',
  });

  useEffect(() => {
    if (className) {
      setProductType(className as string);
    }
  }, [className]);

  if (!imageData) {
    const ProductIcon = PRODUCT_TYPE_ICONS[productType] || PRODUCT_TYPE_ICONS.Unknown;
    return (
      <div className="w-full h-[120px] bg-transparent rounded-xl flex items-center justify-center">
        <ProductIcon className="w-16 h-16 text-gray-600" />
      </div>
    );
  }

  // Handle data URL format
  const imageUrl = imageData.startsWith('data:image') 
    ? imageData 
    : `data:image/jpeg;base64,${imageData}`;

  return (
    <div className="w-full h-[120px] bg-gray-800/50 rounded-lg overflow-hidden">
      <img 
        src={imageUrl}
        alt="Product"
        className="w-full h-full object-cover"
      />
    </div>
  );
}

export default function Products() {
  const navigate = useNavigate();
  const [groupBy, setGroupBy] = useState<GroupBy>('entity');
  const { entities, isLoading } = useEntityDetails();
  const { setSelectedProduct } = useProduct();
  const [currentSlide, setCurrentSlide] = useState(0);

  // Filter out entities with no products
  const filteredEntities = entities?.filter(entity => entity.products.length > 0) || [];

  // Get top 5 products for featured carousel
  const featuredProducts = filteredEntities
    .flatMap(entity => 
      entity.products.map(product => ({
        ...product,
        entityName: entity.name,
        entityAddress: entity.address
      }))
    )
    .slice(0, 5);

  // Auto-cycling carousel
  useEffect(() => {
    if (featuredProducts.length > 1) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % featuredProducts.length);
      }, 20000); // Change slide every 20 seconds (4x longer)

      return () => clearInterval(interval);
    }
  }, [featuredProducts.length]);

  // Group products by type
  const productsByType = filteredEntities.flatMap(entity => 
    entity.products.map(product => ({
      ...product,
      entityName: entity.name,
      entityAddress: entity.address
    }))
  ).reduce((acc, product) => {
    const type = product.className;
    if (!acc[type]) acc[type] = [];
    acc[type].push(product);
    return acc;
  }, {} as Record<string, Array<ProductWithEntity>>) || {};

  // Debug logging for ratings
  console.log('=== PRODUCTS PAGE DEBUG ===');
  console.log('Filtered entities:', filteredEntities.map(e => ({ name: e.name, address: e.address, productCount: e.products.length })));
  console.log('Featured products:', featuredProducts.map(p => ({ 
    name: p.name, 
    address: p.address, 
    entityName: p.entityName, 
    entityAddress: p.entityAddress 
  })));
  console.log('Products by type:', Object.keys(productsByType).map(type => ({ type, count: productsByType[type].length })));
  console.log('==========================');

  const handleProductClick = (product: ProductWithEntity) => {
    navigate(`/product/${product.address}`);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % featuredProducts.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + featuredProducts.length) % featuredProducts.length);
  };

  // If there are no products at all, show a message
  if (isLoading || filteredEntities.length === 0 || !productsByType) {
    return (
      <div className="min-h-screen p-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-lg font-bold mb-3 bg-gradient-to-r from-blue-500/90 via-purple-500/90 to-pink-500/90 text-transparent bg-clip-text">Products</h1>
            </div>
          </div>
          <div className="flex flex-col items-center justify-center min-h-[20vh]">
            <h1 className="text-2xl font-bold text-white mb-2">Go </h1>
            <h2 className="text-lg font-normal text-white">Participate in DeFi launched by the Community</h2>
            <h2 className="text-lg font-normal text-white">*Disclaimer: This is a catalog of products launched by the community.  We are not responsible for any logic, execution, or security of products listed here.</h2>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-4xl font-bold mb-0 bg-gradient-to-r from-blue-500/90 via-purple-500/90 to-pink-500/90 text-transparent bg-clip-text">Products</h1>
      <p className="text-base text-gray-400/40 mb-4">Participate in DeFi launched by the Community</p>



      {/* Featured Carousel */}
      {featuredProducts.length > 0 && (
        <div className="mb-12 ">
          <h2 className="text-sm font-bold text-yellow-500/80 mb-6">Featured</h2>
          <div className="relative w-full aspect-[16/9] overflow-hidden rounded-2xl">
            {/* Carousel Container */}
            <div className="relative w-full h-full overflow-hidden">
              {featuredProducts.map((product, index) => (
                <motion.div
                  key={product.address}
                  className="absolute inset-0 flex items-center justify-center"
                  initial={{ x: index === 0 ? 0 : '100%' }}
                  animate={{ 
                    x: index === currentSlide ? '0%' : (index < currentSlide ? '-100%' : '100%')
                  }}
                  transition={{ duration: 0.5, ease: "easeInOut" }}
                >
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="w-full h-full">
                      <ProductCard 
                        product={product} 
                        entityName={product.entityName}
                        disableHoverScale={true}
                        entityAddress={product.entityAddress}
                      />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Dots Indicator - moved outside carousel container */}
          {featuredProducts.length > 1 && (
            <div className="flex justify-center mt-4">
              <div className="flex space-x-0.5">
                {featuredProducts.map((_, index) => (
                  <div
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`cursor-pointer rounded-full transition-all duration-200 ${
                      index === currentSlide ? 'bg-white' : 'bg-white/30'
                    }`}
                    style={{
                      marginLeft: '3px',
                      marginRight: '3px',
                      width: '50px',
                      height: '5px',
                      minWidth: '10px',
                      minHeight: '5px'
                    }}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Toggle Group - Only show if there are multiple entities with products */}
      {filteredEntities.length > 1 && (
        <div className="flex justify-end items-center mb-0">
          <div className="flex justify-end bg-transparent mb-8">
            <ToggleGroup type="single" value={groupBy} onValueChange={(value) => value && setGroupBy(value as GroupBy)} className="bg-white/10 bg-transparent rounded-lg p-1">
              <ToggleGroupItem value="entity" aria-label="Group by Entity" className="px-4 py-2 bg-transparent border-none rounded-lg data-[state=on]:bg-white/20 data-[state=on]:text-white text-white/60 transition-all">
                By Entity
              </ToggleGroupItem>
              <ToggleGroupItem value="type" aria-label="Group by Type" className="px-4 py-2 bg-transparent border-none rounded-md data-[state=on]:bg-white/20 data-[state=on]:text-white text-white/60 transition-all">
                By Type
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
        </div>
      )}

      <div className="space-y-12">
        {groupBy === 'entity' ? (
          // Group by Entity - Integrated with products in entity cards
          filteredEntities.map((entity) => (
            <motion.div
              className="mb-12 p-6 rounded-2xl bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20 border-white/10 pb-12"
              key={entity.address}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <EntityCard entity={entity} clickable={false} showProducts={true} />
            </motion.div>
          ))
        ) : (
          // Group by Type
          Object.entries(productsByType).map(([type, products]) => (
            <motion.div
            className = "mb-12 p-6 rounded-2xl bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20 border-white/10 pb-12"
              key={type}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
            <h2 className="text-2xl font-bold mb-6 text-white/80">{type}</h2>
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 2xl:grid-cols-10 gap-4">
              {products.map((product) => (
                <ProductCard 
                  key={product.address} 
                  product={product} 
                  entityName={product.entityName}
                  entityAddress={product.entityAddress}
                  hideDetails={false}
                  hideDescription={true}
                />
              ))}
            </div>
          </motion.div>
        ))
        )}
      </div>
    </div>
  );
} 